/**
 * Service Classification decisions inline section (P73.2, #484).
 *
 * Renders inside the Service admin drawer's extraSections row.
 * Operators see the decision history per service, can create a
 * new draft decision + sign off (CRO + Service Owner) on
 * outstanding decisions. Once both signoffs land, the parent
 * Service.classification updates atomically (backend side).
 *
 * Visual structure:
 *   header strip + "New decision" button
 *   list of decisions newest-first, each:
 *     classification chip + workflow_state pill + rationale
 *     CRO + Service Owner sub-rows showing signoff state
 *     "Sign as CRO" / "Sign as Service Owner" buttons (when allowed)
 */
import { useTranslation } from "react-i18next";
import { useMemo, useState, type FormEvent } from "react";

import { Button } from "shared/components/Button";
import { useMe } from "shared/api/useMe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { canRunOperations } from "shared/api/authority";

import {
  CLASSIFICATION_KEYS,
  classificationLabel,
  type ServiceClassification,
} from "shared/vocab/workflowState";
import { SignatureHistory } from "shared/components/SignatureHistory";
type WorkflowState = "draft" | "cro_signed" | "service_owner_signed" | "approved";

// Round 9 C1: lifted to shared/ so the service's own page renders the
// same words this panel does, instead of the column value.
type Classification = ServiceClassification;

const STATE_LABELS: Record<WorkflowState, string> = {
  draft: "Draft",
  cro_signed: "CRO signed",
  service_owner_signed: "Service Owner signed",
  approved: "Approved",
};

const STATE_TONES: Record<WorkflowState, string> = {
  draft: "bg-slate-100 text-slate-700",
  cro_signed: "bg-amber-100 text-amber-900",
  service_owner_signed: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-800",
};

interface Decision {
  id: string;
  service_id: string;
  classification: Classification;
  rationale: string;
  evaluator_name: string | null;
  decided_at: string;
  reviewed_at: string | null;
  next_review_due: string | null;
  cro_signoff_name: string | null;
  cro_signoff_email: string | null;
  cro_signoff_at: string | null;
  cro_signoff_note: string | null;
  service_owner_signoff_name: string | null;
  service_owner_signoff_email: string | null;
  service_owner_signoff_at: string | null;
  service_owner_signoff_note: string | null;
  workflow_state: WorkflowState;
  created_at: string;
  updated_at: string;
}

interface DecisionDraft {
  classification: Classification;
  rationale: string;
  evaluator_name: string;
  decided_at: string;
  reviewed_at: string;
  next_review_due: string;
}

function emptyDraft(): DecisionDraft {
  const today = new Date().toISOString();
  return {
    classification: "ibs_supporting",
    rationale: "",
    evaluator_name: "",
    decided_at: today,
    reviewed_at: "",
    next_review_due: "",
  };
}

interface SignoffDraft {
  name: string;
  email: string;
}

function useDecisions(serviceId: string) {
  return useQuery<Decision[]>({
    queryKey: ["service-classification-decisions", serviceId] as const,
    queryFn: () =>
      api.get<Decision[]>(
        `/v1/admin/foundation/services/${serviceId}/classification-decisions`,
      ),
    enabled: Boolean(serviceId),
  });
}

export function ServiceClassificationDecisionsSection({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useDecisions(rowId);
  // P78: best-effort affordance gating. The signoff buttons show when the
  // user could plausibly succeed -- an admin (gate-off default) OR a holder
  // of the matching attestation realm role (gate-on). The backend is the
  // precise gate; a 403 still surfaces via the modal error.
  const me = useMe();
  const realmRoles = me.data?.realm_roles ?? [];
  const isAdmin = canRunOperations(me.data);
  const canSignCro = isAdmin || realmRoles.includes("cro");
  const canSignServiceOwner = isAdmin || realmRoles.includes("service_owner");
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState<{
    decisionId: string;
    role: "cro" | "service-owner";
  } | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["service-classification-decisions", rowId],
    });

  const createDraft = useMutation({
    mutationFn: async (draft: DecisionDraft) => {
      const body: Record<string, unknown> = {
        classification: draft.classification,
        rationale: draft.rationale,
        decided_at: draft.decided_at,
      };
      if (draft.evaluator_name) body.evaluator_name = draft.evaluator_name;
      if (draft.reviewed_at) body.reviewed_at = draft.reviewed_at;
      if (draft.next_review_due) body.next_review_due = draft.next_review_due;
      return await api.post<Decision>(
        `/v1/admin/foundation/services/${rowId}/classification-decisions`,
        body,
      );
    },
    onSuccess: () => {
      setCreating(false);
      void invalidate();
    },
  });

  const signoff = useMutation({
    mutationFn: async (input: {
      decisionId: string;
      role: "cro" | "service-owner";
      body: SignoffDraft;
    }) => {
      const path = `/v1/admin/foundation/service-classification-decisions/${input.decisionId}/signoff-${input.role}`;
      return await api.patch<Decision>(path, input.body);
    },
    onSuccess: () => {
      setSigning(null);
      void invalidate();
    },
  });

  const deleteDraft = useMutation({
    mutationFn: async (decisionId: string) => {
      await api.delete(
        `/v1/admin/foundation/service-classification-decisions/${decisionId}`,
      );
    },
    onSuccess: () => void invalidate(),
  });

  const decisions = useMemo(() => data ?? [], [data]);

  return (
    <section
      data-testid="service-classification-decisions"
      className="space-y-3 border-t border-slate-200 pt-4 mt-4"
    >
      <header className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("screens.serviceClassificationDecisions.classificationDecisions")}
        </h3>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="scd-new-decision-button"
          className="text-xs font-medium text-brand-navy hover:text-brand-brass"
        >
          {t("screens.serviceClassificationDecisions.newDecision")}
        </button>
      </header>

      {isLoading && (
        <p className="text-xs text-slate-500">
          {t("screens.serviceClassificationDecisions.loadingDecisions")}
        </p>
      )}

      {!isLoading && decisions.length === 0 && (
        <p data-testid="scd-empty" className="text-xs italic text-slate-500">
          {t(
            "screens.serviceClassificationDecisions.noClassificationDecisionsOnThisServiceYet",
          )}
        </p>
      )}

      <ul className="space-y-2">
        {decisions.map((d) => (
          <li
            key={d.id}
            data-testid={`scd-row-${d.id}`}
            className="rounded-md border border-slate-200 bg-white p-3 text-sm"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900">
                {classificationLabel(d.classification)}
              </span>
              <span
                data-testid={`scd-row-state-${d.id}`}
                data-state={d.workflow_state}
                className={[
                  "inline-flex items-center rounded-full px-2 py-0.5",
                  "text-xs font-medium uppercase tracking-wide",
                  STATE_TONES[d.workflow_state],
                ].join(" ")}
              >
                {STATE_LABELS[d.workflow_state]}
              </span>
              {d.workflow_state ===
                t("screens.serviceClassificationDecisions.draft") && (
                <Button
                  variant="danger"
                  onClick={() => deleteDraft.mutate(d.id)}
                  testid={`scd-delete-${d.id}`}
                  className="ms-auto"
                >
                  {t("screens.serviceClassificationDecisions.deleteDraft")}
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-600">{d.rationale}</p>
            <p className="mt-1 text-[11px] text-slate-500">
              {t("screens.serviceClassificationDecisions.decided")}{" "}
              {d.decided_at.slice(0, 10)}
              {d.evaluator_name ? ` · evaluator: ${d.evaluator_name}` : ""}
            </p>

            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <SignoffCell
                role="cro"
                label="CRO"
                name={d.cro_signoff_name}
                email={d.cro_signoff_email}
                at={d.cro_signoff_at}
                disabled={d.workflow_state === "approved" || d.cro_signoff_at != null}
                canSign={canSignCro}
                onClick={() => setSigning({ decisionId: d.id, role: "cro" })}
              />
              <SignoffCell
                role="service-owner"
                label="Service Owner"
                name={d.service_owner_signoff_name}
                email={d.service_owner_signoff_email}
                at={d.service_owner_signoff_at}
                disabled={
                  d.workflow_state === "approved" || d.service_owner_signoff_at != null
                }
                canSign={canSignServiceOwner}
                onClick={() => setSigning({ decisionId: d.id, role: "service-owner" })}
              />
            </div>
            {/* C1 -- the ledger showed who signed and when, and dropped
                what they said. Both comments are on the wire and were
                rendered nowhere, so the reason a service was classified
                the way it was lived only in the database. */}
            <SignatureHistory
              testid={`classification-signatures-${d.id}`}
              entries={[
                {
                  roleLabel: t("roles.cro"),
                  name: d.cro_signoff_name,
                  at: d.cro_signoff_at,
                  note: d.cro_signoff_note,
                  // Round 10 F2 -- no box on either sign leg here.
                  commentOffered: false,
                },
                {
                  roleLabel: t("roles.service_owner"),
                  name: d.service_owner_signoff_name,
                  at: d.service_owner_signoff_at,
                  note: d.service_owner_signoff_note,
                  commentOffered: false,
                },
              ]}
            />
          </li>
        ))}
      </ul>

      {creating && (
        <NewDecisionForm
          onCancel={() => setCreating(false)}
          onSubmit={(draft) => createDraft.mutate(draft)}
          submitting={createDraft.isPending}
          error={createDraft.error?.message ?? null}
        />
      )}

      {signing && (
        <SignoffModal
          role={signing.role}
          onCancel={() => setSigning(null)}
          onSubmit={(body) =>
            signoff.mutate({
              decisionId: signing.decisionId,
              role: signing.role,
              body,
            })
          }
          submitting={signoff.isPending}
          error={signoff.error?.message ?? null}
        />
      )}
    </section>
  );
}

interface SignoffCellProps {
  role: "cro" | "service-owner";
  label: string;
  name: string | null;
  email: string | null;
  at: string | null;
  disabled: boolean;
  canSign: boolean;
  onClick: () => void;
}

function SignoffCell({
  role,
  label,
  name,
  email,
  at,
  disabled,
  canSign,
  onClick,
}: SignoffCellProps) {
  const { t } = useTranslation();
  if (at && name && email) {
    return (
      <div
        data-testid={`scd-signoff-${role}-signed`}
        className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-emerald-900"
      >
        <span className="block text-[10px] uppercase tracking-wide">
          {label} {t("screens.serviceClassificationDecisions.signed")}
        </span>
        <span className="block">{name}</span>
        <span className="block text-[11px] opacity-80">{email}</span>
        <span className="block text-[11px] opacity-80">
          {at.slice(0, 16).replace("T", " ")}
        </span>
      </div>
    );
  }
  // P78: the viewer can't sign in this role -- show a muted note, not a
  // button. (Backend still enforces; this is the affordance only.)
  if (!canSign) {
    return (
      <div
        data-testid={`scd-signoff-${role}-locked`}
        className="rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-400"
      >
        <span className="block text-[10px] uppercase tracking-wide">{label}</span>
        <span className="block text-[11px]">
          {t("screens.serviceClassificationDecisions.requiresThe")} {label}{" "}
          {t("screens.serviceClassificationDecisions.role")}
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`scd-signoff-${role}-button`}
      className={[
        "rounded-md border p-2 text-start",
        disabled
          ? "border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50"
          : "border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100",
      ].join(" ")}
    >
      <span className="block text-[10px] uppercase tracking-wide">{label}</span>
      <span className="block font-medium">
        {t("screens.serviceClassificationDecisions.signOff")}
      </span>
    </button>
  );
}

interface NewDecisionFormProps {
  onCancel: () => void;
  onSubmit: (draft: DecisionDraft) => void;
  submitting: boolean;
  error: string | null;
}

function NewDecisionForm({
  onCancel,
  onSubmit,
  submitting,
  error,
}: NewDecisionFormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DecisionDraft>(emptyDraft);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(draft);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="scd-new-decision-form"
      className="rounded-md border border-indigo-200 bg-indigo-50 p-3 space-y-2 text-sm"
    >
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {t("screens.serviceClassificationDecisions.classification")}
        </label>
        <select
          value={draft.classification}
          onChange={(e) =>
            setDraft({
              ...draft,
              classification: e.target.value as Classification,
            })
          }
          data-testid="scd-new-classification"
          className="w-full rounded border-slate-300 text-sm"
        >
          {(CLASSIFICATION_KEYS as readonly Classification[]).map((value) => (
            <option key={value} value={value}>
              {classificationLabel(value)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {t("screens.serviceClassificationDecisions.rationaleRequired")}
        </label>
        <textarea
          dir="auto"
          value={draft.rationale}
          onChange={(e) => setDraft({ ...draft, rationale: e.target.value })}
          required
          rows={3}
          data-testid="scd-new-rationale"
          className="w-full rounded border-slate-300 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {t("screens.serviceClassificationDecisions.evaluatorOptional")}
        </label>
        <input
          dir="auto"
          type="text"
          value={draft.evaluator_name}
          onChange={(e) => setDraft({ ...draft, evaluator_name: e.target.value })}
          className="w-full rounded border-slate-300 text-sm"
        />
      </div>
      {error && (
        <p data-testid="scd-new-error" className="text-xs text-rose-700">
          {error}
        </p>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs text-slate-700 hover:text-slate-900"
        >
          {t("screens.serviceClassificationDecisions.cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="scd-new-submit"
          className="px-3 py-1 text-xs font-medium rounded bg-brand-navy text-white hover:bg-brand-navyDeep disabled:opacity-60"
        >
          {submitting
            ? t("screens.serviceClassificationDecisions.creating")
            : t("screens.serviceClassificationDecisions.createDraft")}
        </button>
      </div>
    </form>
  );
}

interface SignoffModalProps {
  role: "cro" | "service-owner";
  onCancel: () => void;
  onSubmit: (body: SignoffDraft) => void;
  submitting: boolean;
  error: string | null;
}

function SignoffModal({
  role,
  onCancel,
  onSubmit,
  submitting,
  error,
}: SignoffModalProps) {
  const { t } = useTranslation();
  const me = useMe();
  // B3 -- this form used to ask the signer to TYPE a name and email,
  // so the register recorded whatever was typed while the inbox
  // recorded the session. One person, two identities, neither record
  // reconcilable with the other. The session signs here too; the form
  // shows who that is, so nobody signs without seeing whose name goes
  // on it.
  const signerName = me.data?.display_name ?? me.data?.username ?? "";
  const signerEmail = me.data?.email ?? "";
  const attributable = signerName !== "" && signerEmail !== "";
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit({ name: signerName, email: signerEmail });
  };
  const label = role === "cro" ? "CRO" : "Service Owner";

  return (
    <form
      onSubmit={submit}
      data-testid={`scd-signoff-form-${role}`}
      className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2 text-sm"
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
        {t("screens.serviceClassificationDecisions.signOffAs")} {label}
      </h4>
      {attributable ? (
        <p data-testid={`scd-signoff-${role}-signer`} className="text-xs">
          {t("screens.serviceClassificationDecisions.signingAs", {
            name: signerName,
            email: signerEmail,
          })}
        </p>
      ) : (
        <p
          data-testid={`scd-signoff-${role}-unattributable`}
          className="text-xs text-amber-900"
        >
          {t("myWork.action.noIdentity")}
        </p>
      )}
      {error && (
        <p data-testid={`scd-signoff-${role}-error`} className="text-xs text-rose-700">
          {error}
        </p>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs text-slate-700 hover:text-slate-900"
        >
          {t("screens.serviceClassificationDecisions.cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting || !attributable}
          data-testid={`scd-signoff-${role}-submit`}
          className="px-3 py-1 text-xs font-medium rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60"
        >
          {submitting
            ? t("screens.serviceClassificationDecisions.signing")
            : t("screens.serviceClassificationDecisions.confirmSignoff", {
                role: label,
              })}
        </button>
      </div>
    </form>
  );
}
